import { createFileRoute } from '@tanstack/react-router';
import { StatCard } from '@/components/ui/StatCard';
import { cn } from '@/lib/utils';
import { SealCheck, ChartDonut, TreeStructure, CloudCheck, Cloud } from '@phosphor-icons/react';

const SOURCES_DATA = [
  { source: 'Chargebee', records: '4.2M', errorRate: '0.02%', updated: '2m ago' },
  { source: 'Braintree', records: '3.1M', errorRate: '0.05%', updated: '5m ago' },
  { source: 'Checkout.com', records: '2.8M', errorRate: '0.08%', updated: '15m ago' },
  { source: 'Recurly', records: '1.9M', errorRate: '0.01%', updated: '3m ago' },
  { source: 'PayPal', records: '1.4M', errorRate: '0.12%', updated: '10m ago' },
  { source: 'Vantiv', records: '0.2M', errorRate: '—', updated: '— (paused)' },
];

const BAR_CHART_1 = [
  { label: 'CB', height: 85, color: 'bg-primary' },
  { label: 'BT', height: 62, color: 'bg-blue-500' },
  { label: 'CKO', height: 56, color: 'bg-emerald-500' },
  { label: 'REC', height: 38, color: 'bg-amber-500' },
  { label: 'PP', height: 28, color: 'bg-purple-500' },
  { label: 'VNT', height: 4, color: 'bg-slate-300' },
];

const BAR_CHART_2 = [
  { label: 'Jan', height: 88 },
  { label: 'Feb', height: 91 },
  { label: 'Mar', height: 89 },
  { label: 'Apr', height: 93 },
  { label: 'May', height: 95 },
  { label: 'Jun', height: 94 },
  { label: 'Jul', height: 96 },
];

function AnalyticsPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Analytics Preview</h2>
        <p className="text-slate-500 text-sm mt-1">
          Data quality metrics, source coverage, and schema health across all connected platforms.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Data Quality Score" value="96.2%" icon={SealCheck} iconColor="text-green-600" change="+0.4%" />
        <StatCard label="Coverage Rate" value="89.1%" icon={ChartDonut} iconColor="text-blue-600" change="+2.1%" />
        <StatCard label="Schema Drift" value="2 fields" icon={TreeStructure} iconColor="text-amber-500" />
        <StatCard label="Active Sources" value={6} icon={CloudCheck} iconColor="text-primary" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Records by Source */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-1">Records by Source</h3>
          <p className="text-xs text-slate-400 mb-6">Total records ingested per platform</p>
          <div className="flex items-end gap-4 h-44">
            {BAR_CHART_1.map((bar) => (
              <div key={bar.label} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className={cn('w-full rounded-t-md transition-colors', bar.color)}
                  style={{ height: `${bar.height}%` }}
                />
                <span className="text-xs text-slate-400 font-medium">{bar.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Data Quality Trend */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-1">Data Quality Trend</h3>
          <p className="text-xs text-slate-400 mb-6">Monthly quality score (higher is better)</p>
          <div className="flex items-end gap-3 h-44">
            {BAR_CHART_2.map((bar) => (
              <div key={bar.label} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full rounded-t-md bg-emerald-500/80 hover:bg-emerald-500 transition-colors"
                  style={{ height: `${bar.height}%` }}
                />
                <span className="text-xs text-slate-400 font-medium">{bar.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sources Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">Source Breakdown</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-6 py-3">Source</th>
              <th className="text-right px-6 py-3">Total Records</th>
              <th className="text-right px-6 py-3">Error Rate</th>
              <th className="text-right px-6 py-3">Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {SOURCES_DATA.map((s) => (
              <tr key={s.source} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Cloud size={18} weight="regular" className="text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-slate-900">{s.source}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-700 text-right font-medium">{s.records}</td>
                <td className="px-6 py-4 text-sm text-right">
                  <span className={cn(
                    'font-medium',
                    s.errorRate === '—' ? 'text-slate-400' : parseFloat(s.errorRate) > 0.1 ? 'text-amber-600' : 'text-green-600',
                  )}>
                    {s.errorRate}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-500 text-right">{s.updated}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/analytics')({
  component: AnalyticsPage,
});
